// src/pages/masterdata/item/ItemForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  FolderOpen, 
  Package, 
  AlertCircle,
  Info,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import itemService from '../../../services/itemService';

const ItemForm = ({ mode = 'create', item = null, onClose, onSave }) => {
  // Form state
  const [formData, setFormData] = useState({
    itemNumber: '',
    itemCode: '',
    itemName: '',
    isGroup: false,
    parentId: null,
    itemType: 'Material',
    unit: '',
    defaultPrice: '',
    description: '',
    specification: '',
    remarks: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parentItems, setParentItems] = useState([
    { id: null, itemNumber: 'ROOT', itemName: '[ No Parent - Root Level ]', level: -1 }
  ]);

  // Unit options (categorized)
  const unitOptions = [
    { value: '', label: '-- Select Unit --', disabled: true },
    { group: 'Length', options: [
      { value: 'm', label: 'm - Meter' },
      { value: 'km', label: 'km - Kilometer' },
      { value: 'cm', label: 'cm - Centimeter' }
    ]},
    { group: 'Weight', options: [
      { value: 'kg', label: 'kg - Kilogram' },
      { value: 'gram', label: 'gram - Gram' },
      { value: 'ton', label: 'ton - Ton' }
    ]},
    { group: 'Volume', options: [
      { value: 'liter', label: 'liter - Liter' },
      { value: 'm3', label: 'm3 - Meter Kubik' },
      { value: 'zak', label: 'zak - zak' }
    ]},
    { group: 'Area', options: [
      { value: 'm2', label: 'm2 - Meter Persegi' },
      { value: 'ha', label: 'ha - Hektar' }
    ]},
    { group: 'Count/Unit', options: [
      { value: 'unit', label: 'unit - Unit' },
      { value: 'pcs', label: 'pcs - Pieces' },
      { value: 'set', label: 'set - Set' },
      { value: 'box', label: 'box - Box' },
      { value: 'roll', label: 'roll - Roll' },
      { value: 'lbr', label: 'lbr - Lembar' },
      { value: 'bundle', label: 'bundle - Bundle' },
      { value: 'btg', label: 'btg - Batang' },
      { value: 'KK', label: 'KK - Kepala Keluarga' },
      { value: 'tube', label: 'tube - tube' },            
      { value: 'pak', label: 'pak - pak' },
      { value: 'VA', label: 'VA - Volt Ampere' }                  
    ]},
    { group: 'Service', options: [
      { value: 'ls', label: 'ls - Lump Sum' },
      { value: 'hours', label: 'hours - Hours' },
      { value: 'days', label: 'days - Days' },
      { value: 'month', label: 'month - Month' },
      { value: 'pkg', label: 'pkg - Package' }
    ]}
  ];

  // Fetch parent items on mount
  useEffect(() => {
    fetchParentItems();
  }, []);

  // Initialize form data based on mode
  useEffect(() => {
    if (mode === 'edit' || mode === 'view') {
      if (item) {
        setFormData({
          itemNumber: item.itemNumber || '',
          itemCode: item.itemCode || '',
          itemName: item.itemName || '',
          isGroup: item.isGroup || false,
          parentId: item.parentId || null,
          itemType: item.itemType || 'Material',
          unit: item.unit || '',
          defaultPrice: item.defaultPrice || '',
          description: item.description || '',
          specification: item.specification || '',
          remarks: item.remarks || '',
          isActive: item.isActive !== undefined ? item.isActive : true
        });
      }
    } else if (mode === 'create' && item) {
      // Creating child of selected item
      const generateNumber = async () => {
        const suggestedNumber = await generateChildNumber(item.itemNumber, item.id);
        setFormData(prev => ({
          ...prev,
          parentId: item.id,
          itemType: item.itemType, // Inherit parent's type
          itemNumber: suggestedNumber // Auto-suggest number
        }));
      };
      generateNumber();
    }
  }, [mode, item]);

  // Fetch parent items from API
  const fetchParentItems = async () => {
    try {
      const groups = await itemService.getGroups();
      setParentItems([
        { id: null, itemNumber: 'ROOT', itemName: '[ No Parent - Root Level ]', level: -1 },
        ...groups.map(g => ({
          id: g.id,
          itemNumber: g.itemNumber,
          itemCode: g.itemCode,
          itemName: g.itemName,
          level: g.level,
          itemType: g.itemType
        }))
      ]);
    } catch (error) {
      console.error('Error fetching parent items:', error);
    }
  };

  // Generate child number based on parent
  const generateChildNumber = async (parentNumber, parentId) => {
    try {
      // Get existing children to determine next sequence
      const children = await itemService.getChildrenByParentId(parentId);
      
      if (children.length === 0) {
        return `${parentNumber}.001`;
      }
      
      // Find highest sequence number
      const sequences = children
        .map(child => {
          const parts = child.itemNumber.split('.');
          return parseInt(parts[parts.length - 1]) || 0;
        })
        .filter(num => !isNaN(num));
      
      const maxSequence = Math.max(...sequences, 0);
      const nextSequence = (maxSequence + 1).toString().padStart(3, '0');
      
      return `${parentNumber}.${nextSequence}`;
    } catch (error) {
      console.error('Error generating child number:', error);
      return `${parentNumber}.001`; // Fallback
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Check item number availability (debounced)
    if (name === 'itemNumber' && mode !== 'view' && mode !== 'edit') {
      clearTimeout(window.itemNumberCheckTimeout);
      window.itemNumberCheckTimeout = setTimeout(() => {
        checkItemNumberAvailability(value);
      }, 500);
    }

    // Check item code availability (debounced) - only if provided
    if (name === 'itemCode' && mode !== 'view' && value.trim()) {
      clearTimeout(window.itemCodeCheckTimeout);
      window.itemCodeCheckTimeout = setTimeout(() => {
        checkItemCodeAvailability(value);
      }, 500);
    }
  };

  // Check if item number exists
  const checkItemNumberAvailability = async (number) => {
    if (!number.trim()) return;
    
    try {
      const exists = await itemService.checkItemNumber(number);
      
      if (exists) {
        setErrors(prev => ({
          ...prev,
          itemNumber: 'Item number already exists'
        }));
      }
    } catch (error) {
      console.error('Error checking item number:', error);
    }
  };

  // Check if item code exists (only if provided)
  const checkItemCodeAvailability = async (code) => {
    if (!code.trim()) return;
    
    try {
      let exists;
      if (mode === 'edit' && item) {
        exists = await itemService.checkItemCodeExcludingId(code, item.id);
      } else {
        exists = await itemService.checkItemCode(code);
      }
      
      if (exists) {
        setErrors(prev => ({
          ...prev,
          itemCode: 'Item code already exists'
        }));
      }
    } catch (error) {
      console.error('Error checking item code:', error);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Item number validation
    if (!formData.itemNumber.trim()) {
      newErrors.itemNumber = 'Item number is required';
    } else if (!/^[0-9.]+$/.test(formData.itemNumber)) {
      newErrors.itemNumber = 'Item number must contain only numbers and dots';
    }
    
    // Item code validation (only if provided)
    if (formData.itemCode && formData.itemCode.trim()) {
      if (!/^[A-Z0-9\-_]+$/.test(formData.itemCode)) {
        newErrors.itemCode = 'Item code must contain only uppercase letters, numbers, hyphens, and underscores';
      }
    }
    
    // Item name validation
    if (!formData.itemName.trim()) {
      newErrors.itemName = 'Item name is required';
    }
    
    // For actual items (not groups), unit and price are required
    if (!formData.isGroup) {
      if (!formData.unit) {
        newErrors.unit = 'Unit is required for actual items';
      }
      if (!formData.defaultPrice || formData.defaultPrice <= 0) {
        newErrors.defaultPrice = 'Default price is required and must be greater than 0';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const itemPayload = {
        itemNumber: formData.itemNumber,
        itemCode: formData.itemCode.trim() || null,
        itemName: formData.itemName,
        isGroup: formData.isGroup,
        parentId: formData.parentId || null,
        itemType: formData.itemType,
        unit: formData.isGroup ? null : formData.unit,
        defaultPrice: formData.isGroup ? null : (formData.defaultPrice ? Number(formData.defaultPrice) : null),
        description: formData.description || null,
        specification: formData.specification || null,
        remarks: formData.remarks || null,
        isActive: formData.isActive
      };
      
      if (mode === 'create') {
        await itemService.createItem(itemPayload);
        toast.success('Item created successfully!');
      } else if (mode === 'edit') {
        // For update, use UpdateItemRequest (cannot change number, type, parent)
        const updatePayload = {
          itemCode: formData.itemCode.trim() || null,
          itemName: formData.itemName,
          unit: formData.isGroup ? null : formData.unit,
          defaultPrice: formData.isGroup ? null : (formData.defaultPrice ? Number(formData.defaultPrice) : null),
          description: formData.description || null,
          specification: formData.specification || null,
          remarks: formData.remarks || null,
          isActive: formData.isActive
        };
        
        await itemService.updateItem(item.id, updatePayload);
        toast.success('Item updated successfully!');
      }
      
      onSave(); // Callback to refresh list
    } catch (error) {
      console.error('Error submitting form:', error);
      // Error already handled by axios interceptor with toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const getTitle = () => {
    if (isViewMode) return 'View Item Details';
    if (isEditMode) return 'Edit Item';
    return item ? `Create Child Item under "${item.itemName}"` : 'Create New Root Item';
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {formData.isGroup ? (
              <FolderOpen className="w-6 h-6" />
            ) : (
              <Package className="w-6 h-6" />
            )}
            <div>
              <h2 className="text-xl font-bold">{getTitle()}</h2>
              <p className="text-red-100 text-sm mt-0.5">
                {isViewMode ? 'View item information' : isEditMode ? 'Update item information' : 'Fill in item details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Info Alert for Create Mode */}
            {isCreateMode && item && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">Creating Child Item</p>
                  <p className="text-sm text-blue-700 mt-1">
                    This item will be created under: <span className="font-semibold">{item.itemNumber} - {item.itemName}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Item Type Toggle */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isGroup"
                  checked={formData.isGroup}
                  onChange={handleChange}
                  disabled={isViewMode || isEditMode}
                  className="w-5 h-5 text-red-600 rounded focus:ring-red-500 disabled:opacity-50"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">
                    This is a Group Item (Folder)
                  </div>
                  <div className="text-sm text-gray-600 mt-0.5">
                    Group items can contain child items but cannot have unit or price
                  </div>
                </div>
                {formData.isGroup ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                    GROUP
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                    ITEM
                  </span>
                )}
              </label>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Parent Item */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Parent Item
                  <span className="text-gray-500 font-normal ml-1">(optional)</span>
                </label>
                <select
                  name="parentId"
                  value={formData.parentId || ''}
                  onChange={handleChange}
                  disabled={isViewMode || isEditMode || (isCreateMode && item)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600"
                >
                  {parentItems.map(parent => (
                    <option key={parent.id || 'root'} value={parent.id || ''}>
                      {'  '.repeat(parent.level + 1)}{parent.itemNumber} - {parent.itemName}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select parent item to create hierarchical structure
                </p>
              </div>

              {/* Item Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="itemNumber"
                  value={formData.itemNumber}
                  onChange={handleChange}
                  disabled={isViewMode || isEditMode}
                  placeholder="e.g., 001.001.001"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600 font-mono ${
                    errors.itemNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.itemNumber && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.itemNumber}
                  </p>
                )}
                {isCreateMode && !isEditMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    Hierarchical numbering (numbers and dots only)
                  </p>
                )}
                {isEditMode && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Item number cannot be changed after creation
                  </p>
                )}
              </div>

              {/* Item Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Code
                  <span className="text-gray-500 font-normal ml-1">(optional)</span>
                </label>
                <input
                  type="text"
                  name="itemCode"
                  value={formData.itemCode}
                  onChange={handleChange}
                  disabled={isViewMode}
                  placeholder="e.g., KBL-I2C-24"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600 font-mono ${
                    errors.itemCode ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.itemCode && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.itemCode}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Custom identifier (uppercase, numbers, hyphens, underscores)
                </p>
              </div>

              {/* Item Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="itemType"
                  value={formData.itemType}
                  onChange={handleChange}
                  disabled={isViewMode || isEditMode || (isCreateMode && item)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600"
                >
                  <option value="Material">Material</option>
                  <option value="Service">Service</option>
                </select>
                {isCreateMode && item && (
                  <p className="text-xs text-gray-500 mt-1">
                    Inherited from parent item
                  </p>
                )}
                {isEditMode && (
                  <p className="text-xs text-amber-600 mt-1">
                    Item type cannot be changed
                  </p>
                )}
              </div>

              {/* Item Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Item Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleChange}
                  disabled={isViewMode}
                  placeholder="Enter item name"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600 ${
                    errors.itemName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.itemName && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.itemName}
                  </p>
                )}
              </div>
            </div>

            {/* Unit & Price (Only for actual items) */}
            {!formData.isGroup && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-blue-900 font-semibold">
                  <Package className="w-5 h-5" />
                  <span>Item Details (Required for Actual Items)</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Unit */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Unit <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                      disabled={isViewMode}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600 ${
                        errors.unit ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">-- Select Unit --</option>
                      {unitOptions.map((group, idx) => {
                        if (group.group) {
                          return (
                            <optgroup key={idx} label={group.group}>
                              {group.options.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </optgroup>
                          );
                        }
                        return null;
                      })}
                    </select>
                    {errors.unit && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.unit}
                      </p>
                    )}
                  </div>

                  {/* Default Price */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Default Price <span className="text-red-500">*</span>
                    </label>
                    {isViewMode ? (
                      <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 font-semibold">
                        {formatCurrency(formData.defaultPrice)}
                      </div>
                    ) : (
                      <input
                        type="number"
                        name="defaultPrice"
                        value={formData.defaultPrice}
                        onChange={handleChange}
                        placeholder="0"
                        min="0"
                        step="1000"
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                          errors.defaultPrice ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    )}
                    {errors.defaultPrice && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.defaultPrice}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-gray-600" />
                Additional Information
              </h3>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isViewMode}
                  rows={3}
                  placeholder="Enter item description"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600"
                />
              </div>

              {/* Specification (Only for actual items) */}
              {!formData.isGroup && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Specification
                  </label>
                  <textarea
                    name="specification"
                    value={formData.specification}
                    onChange={handleChange}
                    disabled={isViewMode}
                    rows={3}
                    placeholder="Enter technical specifications"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600"
                  />
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  disabled={isViewMode}
                  rows={2}
                  placeholder="Additional notes"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-600"
                />
              </div>

              {/* Active Status */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    disabled={isViewMode}
                    className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Active</div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      Inactive items won't appear in selection lists
                    </div>
                  </div>
                  {formData.isActive ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      ACTIVE
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                      INACTIVE
                    </span>
                  )}
                </label>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </button>
          
          {!isViewMode && (
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditMode ? 'Update Item' : 'Create Item'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemForm;